import { createContext, useMemo, useState } from 'react';

import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { useSaveBarEntry } from '@nucleus-commerce/admin-kit/save-bar';
import type { StoreVariantPricing } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';

import {
  draftFromRow,
  isDraftDirty,
  toBulkItems,
  type PricingDraft,
} from '../schemas/pricing-rules';

import { useBulkVariantPricing, useStorePricing } from './use-store-pricing';

/** A refused field of a row: `field` is the draft's. */
export interface StorePricingRowError {
  field: string;
  message: string;
}

const NO_ROW_ERRORS: ReadonlyMap<string, StorePricingRowError> = new Map();

export interface StorePricingDrafts {
  /** The store's pricing row of a variant; undefined until loaded or for a variant added since. */
  rowOf: (variantId: string) => StoreVariantPricing | undefined;
  draftFor: (row: StoreVariantPricing) => PricingDraft;
  setDraft: (variantId: string, draft: PricingDraft) => void;
  rows: StoreVariantPricing[];
  canWrite: boolean;
  /** The store currency; every override is entered in it. */
  currency: string;
  saving: boolean;
  saveError: unknown;
  /** What a failed save refused, by variant. */
  rowErrors: ReadonlyMap<string, StorePricingRowError>;
  isPending: boolean;
  queryError: unknown;
  refetch: () => void;
}

/** Lets the store columns of the variant table reach the drafts. */
export const StorePricingContext = createContext<StorePricingDrafts | null>(null);

/**
 * The store's prices of a product's variants as drafts: edited in the variant table's store
 * columns, saved together through the save bar next to the master edits (ADR 97).
 */
export function useStorePricingDrafts(
  storeId: string,
  storeProductId: string,
  currency: string,
): StorePricingDrafts {
  const { can } = usePermissions();
  const canWrite = can('products.write');
  const query = useStorePricing(storeId, storeProductId);
  const saveAll = useBulkVariantPricing(storeId, storeProductId);
  const [drafts, setDrafts] = useState<Map<string, PricingDraft>>(new Map());
  const [rowErrors, setRowErrors] = useState(NO_ROW_ERRORS);

  const rows = useMemo(() => query.data ?? [], [query.data]);
  const byVariant = useMemo(() => new Map(rows.map((row) => [row.variantId, row])), [rows]);
  const draftFor = (row: StoreVariantPricing) => drafts.get(row.variantId) ?? draftFromRow(row);
  const dirty = rows.some((row) => isDraftDirty(draftFor(row), row));

  const save = () => {
    const items = toBulkItems(drafts, rows);
    if (items.length === 0) return;
    saveAll.mutate(
      { items },
      {
        onSuccess: () => {
          setRowErrors(NO_ROW_ERRORS);
          setDrafts((current) => {
            const next = new Map(current);
            for (const item of items) next.delete(item.variantId);
            return next;
          });
        },
        onError: (error) => {
          if (!isApiError(error)) return;
          const refused = new Map<string, StorePricingRowError>();
          for (const detail of error.details) {
            const match = /^body\.items\.(\d+)\.([A-Za-z]+)/.exec(detail.field);
            const variantId = match ? items[Number(match[1])]?.variantId : undefined;
            if (variantId && match?.[2] && !refused.has(variantId)) {
              refused.set(variantId, { field: match[2], message: detail.message });
            }
          }
          setRowErrors(refused);
        },
      },
    );
  };

  useSaveBarEntry({
    dirty,
    saving: saveAll.isPending,
    onSave: save,
    onDiscard: () => {
      setDrafts(new Map());
      setRowErrors(NO_ROW_ERRORS);
    },
    disabled: !canWrite,
  });

  return {
    rowOf: (variantId) => byVariant.get(variantId),
    draftFor,
    setDraft: (variantId, draft) => {
      setDrafts((current) => new Map(current).set(variantId, draft));
      setRowErrors((current) => {
        if (!current.has(variantId)) return current;
        const next = new Map(current);
        next.delete(variantId);
        return next;
      });
    },
    rows,
    canWrite,
    currency,
    saving: saveAll.isPending,
    saveError: saveAll.error,
    rowErrors,
    isPending: query.isPending,
    queryError: query.error,
    refetch: () => void query.refetch(),
  };
}
